import { useState, useEffect } from "react";
import { useLocalStorage } from "./useLocalStorage";

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstallCard, setShowIOSInstallCard] = useState(false);
  const [isIOS] = useState(() => /iPhone|iPad|iPod/i.test(navigator.userAgent));
  const [timeThresholdMet, setTimeThresholdMet] = useState(false);

  const [promptShown, setPromptShown] = useLocalStorage(
    "pwa-install-prompt-shown",
    false
  );

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  // Track time spent - show prompt after 10 seconds
  useEffect(() => {
    if (promptShown) return; // Don't track if prompt already shown

    const timer = setTimeout(() => {
      setTimeThresholdMet(true);
    }, 10000); // 10 seconds

    return () => {
      clearTimeout(timer);
    };
  }, [promptShown]);

  // Compute if prompt should be shown based on conditions
  const shouldShowPrompt = !promptShown && timeThresholdMet;

  const installPWA = async () => {
    setPromptShown(true);

    if (deferredPrompt) {
      // For browsers that support PWA install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        setPromptShown(true);
        return { success: true, outcome: "accepted" };
      } else {
        return { success: false, outcome: "dismissed" };
      }
    } else if (isIOS) {
      // For iOS, show instructions
      setShowIOSInstallCard(true);
      return { success: false, outcome: "ios_instructions_shown" };
    } else {
      // Browser doesn't support PWA install
      return { success: false, outcome: "not_supported" };
    }
  };

  const closeIOSInstructions = () => {
    setShowIOSInstallCard(false);
  };

  const resetPrompt = () => {
    setPromptShown(false);
    setDeferredPrompt(null);
    setShowIOSInstallCard(false);
    setTimeThresholdMet(false);
  };

  return {
    installPWA,
    deferredPrompt,
    showIOSInstallCard,
    isIOS,
    promptShown,
    closeIOSInstructions,
    resetPrompt,
    canInstall: !!deferredPrompt || isIOS,
    setPromptShown,
    shouldShowPrompt,
  };
};
